import { Plus } from 'lucide-react';
import React, { createContext, useContext } from 'react';
import {
	Tag as AriaTag,
	TagGroup as AriaTagGroup,
	type TagGroupProps as AriaTagGroupProps,
	type TagProps as AriaTagProps,
	Button,
	TagList,
	type TagListProps,
	Text,
	composeRenderProps
} from 'react-aria-components';
import { cn, focusRing } from './utils';
import { FieldLabel } from '../forms/controls/base-controls/FieldLabel';
import { FieldDescription } from '../forms/controls/base-controls/FieldDescription';

const colors = {
	gray: 'bg-gray-100 text-gray-700 border-gray-200 hover:border-gray-300',
	primary: 'bg-primary-500/10 text-primary-500 border-primary-500/20 hover:border-primary-500/30',
	yellow: 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-300',
	blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-300',
	red: 'bg-red-50 text-red-700 border-red-200 hover:border-red-300',
	green: 'bg-green-50 text-green-700 border-green-200 hover:border-green-300'
};

type Color = keyof typeof colors;
const ColorContext = createContext<Color>('gray');

export interface TagGroupProps<T> extends Omit<AriaTagGroupProps, 'children'>, Pick<TagListProps<T>, 'items' | 'children' | 'renderEmptyState'> {
	color?: Color;
	label?: string;
	description?: string;
	errorMessage?: string;
}

export interface TagProps extends AriaTagProps {
	color?: Color;
}

export function TagGroup<T extends object>({ label, description, errorMessage, items, children, renderEmptyState, ...props }: TagGroupProps<T>) {
	return (
		<AriaTagGroup {...props} className={cn('flex flex-col gap-2', props.className)}>
			{label && <FieldLabel>{label}</FieldLabel>}
			<ColorContext.Provider value={props.color || 'gray'}>
				<TagList items={items} renderEmptyState={renderEmptyState} className="flex flex-wrap gap-2">
					{children}
				</TagList>
			</ColorContext.Provider>
			{description && <FieldDescription>{description}</FieldDescription>}
			{errorMessage && (
				<Text slot="errorMessage" className="text-sm text-red-600">
					{errorMessage}
				</Text>
			)}
		</AriaTagGroup>
	);
}

export function Tag({ children, color, ...props }: TagProps) {
	const textValue = typeof children === 'string' ? children : undefined;
	const groupColor = useContext(ColorContext);
	const resolvedColor = color || groupColor;
	return (
		<AriaTag
			textValue={textValue}
			{...props}
			className={composeRenderProps(props.className, (className, renderProps) =>
				cn(
					'flex max-w-fit cursor-default items-center gap-1 rounded-full border px-3 py-1 text-sm transition',
					focusRing(renderProps),
					renderProps.allowsRemoving && 'pr-1',
					renderProps.isSelected && 'border-transparent bg-primary-500 text-white',
					renderProps.isDisabled && 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300',
					!renderProps.isSelected && !renderProps.isDisabled && colors[resolvedColor],
					className
				)
			)}
		>
			{renderProps => (
				<>
					{typeof children === 'function' ? children(renderProps) : children}
					{renderProps.allowsRemoving && (
						<Button
							slot="remove"
							className={btnRenderProps =>
								cn(
									'flex cursor-default items-center justify-center rounded-full p-1 transition-colors hover:bg-black/5 active:bg-black/10',
									focusRing(btnRenderProps)
								)
							}
						>
							<Plus aria-hidden className="size-4" />
						</Button>
					)}
				</>
			)}
		</AriaTag>
	);
}
